import uniq from 'lodash/uniq';
import map from 'lodash/map';
import union from 'lodash/union';

import { CartStore, LineItem, Order } from 'apps/cart/types';

import { variantWithSku } from 'common/selectors';
import { buildLineItemPricing } from './utils';

export const getLineItemsProcessing = (state: CartStore) =>
  state.lineItemsProcessing;

export const getLineItemProcessing = (state: CartStore, lineItem: LineItem) =>
  state.lineItemsProcessing.find(
    item => item.offeringId === lineItem.variant_offering_id
  ) || null;

export const getLineItemProcessingQuantity = (
  lineItem: LineItem,
  state: CartStore
) => {
  const lineItemProcessing = getLineItemProcessing(state, lineItem);
  return lineItemProcessing ? lineItemProcessing.quantity : null;
};

export const getCommitmentRewardLineItems = (state: CartStore) =>
  state.currentOrder.line_items.filter(
    lineItem =>
      Math.abs(lineItem.commitment_reward_adjustment_total as number) !== 0
  );

export const isLineItemSaving = (lineItem: LineItem, state: CartStore) =>
  state.variantsSaving.some(
    item => item.sku === lineItem.sku && item.offeringId === lineItem.offeringId
  );

export const productWithId = (id: string, state: CartStore) =>
  state.products.find(v => v.id === id);

export const orderLineItemWithSku = (
  order: Order,
  sku: string,
  offeringId: number
) =>
  order.line_items.find(
    item => item.sku === sku && item.variant_offering_id === offeringId
  );

export const savedItemWithSku = (
  sku: string,
  state: CartStore,
  offeringId: number
) => orderLineItemWithSku(state.currentOrder, sku, offeringId);

export const hasOrderLineItemWithOfferingId = (
  order: Order,
  offeringId: number
) => order.line_items.some(item => item.variant_offering_id === offeringId);

export const getSidebarFocusState = (state: CartStore) =>
  state.sidebarIsFocused;

export const getCartOpenState = (state: CartStore) => state.isOpen;

export const getSubscriptionConflict = (state: CartStore) =>
  state.userIsSubscribing && !state.canBecomeMember;

export const getShippingCost = ({
  currentOrder,
  freeShippingAmount,
  standardShippingCost,
}) => {
  const itemTotalCents = currentOrder.item_total_fractional;
  const hasFreeShipping =
    currentOrder.membership_order ||
    (freeShippingAmount && itemTotalCents >= freeShippingAmount);
  return hasFreeShipping ? 0 : standardShippingCost;
};

export const getPendingItemQuantityDiff = (
  pendingItem: LineItem,
  state: CartStore
) => {
  const savedLineItem = savedItemWithSku(
    pendingItem.sku,
    state,
    pendingItem.offeringId
  );
  const savedQuantity = savedLineItem ? savedLineItem.quantity : 0;
  return pendingItem.quantity - savedQuantity;
};

export const getPendingQuantity = (state: CartStore) =>
  state.lineItemsPending.reduce(
    (total: number, item: LineItem) =>
      total + getPendingItemQuantityDiff(item, state),
    0
  );

export const getPendingQuantitiesByLineItem = (state: CartStore) => {
  const pendingQuantities = state.lineItemsPending.map((item: LineItem) => ({
    sku: item.sku,
    quantity: getPendingItemQuantityDiff(item, state),
    offeringId: item.offeringId,
  }));

  return pendingQuantities.length ? pendingQuantities : 0;
};

export const getOrderLineItemQuantityDiff = (
  orderPrev: Order,
  orderNew: Order
) => {
  // Map over the orders and grab the indivdual skus and corresponding offering ids
  // Calling toString on the values so we can more easily get a uniq array in the
  // following lines
  const offeringsPrev = map(orderPrev.line_items, item =>
    [item.sku, item.variant_offering_id].toString()
  );
  const offeringsNew = map(orderNew.line_items, item =>
    [item.sku, item.variant_offering_id].toString()
  );

  // Combine the offerings from both new and old orders to get a uniq list of items from both
  const offerings = uniq(union(offeringsPrev, offeringsNew));

  return offerings.map(offerString => {
    // Split the offer from a string to an array so we can use the
    // id and sku
    const offer = offerString.split(',');
    const sku = offer[0];
    const offeringId = parseInt(offer[1]);

    // Set up and return the line items with their quantities
    const lineItemDefault = { sku, offeringId, quantity: 0 };
    const lineItemPrev =
      orderLineItemWithSku(orderPrev, sku, offeringId) || lineItemDefault;
    const lineItemNew =
      orderLineItemWithSku(orderNew, sku, offeringId) || lineItemDefault;
    const quantityDiff = lineItemNew.quantity - lineItemPrev.quantity;
    return { sku, offeringId, quantityDiff, lineItemNew, lineItemPrev };
  });
};

export const getPendingPrice = (state: CartStore) => {
  const { membershipDiscountRate, currentOrder, lineItemsPending } = state;
  const isMembershipOrder = currentOrder.membership_order;
  return lineItemsPending.reduce((total, item) => {
    const variant = variantWithSku(item.sku, state);
    const hasMembershipDiscount = isMembershipOrder && variant.is_subscribable;
    const discountRate = hasMembershipDiscount ? 1 - membershipDiscountRate : 1;
    const pricePerItem = parseFloat((variant.price * discountRate).toFixed(2));
    return total + pricePerItem * getPendingItemQuantityDiff(item, state);
  }, 0);
};

export const reportMissingLineItems = (state: CartStore) => {
  const error = new Error('state.currentOrder.line_items is undefined');

  if (typeof global.bugsnagClient !== 'undefined') {
    global.bugsnagClient.notify(error, {
      metaData: {
        reduxState: state,
      },
    });
  }
};

export const getTotalSavingsForLineItems = (lineItems: LineItem[]) =>
  lineItems.reduce(
    (total, lineItem) =>
      total + buildLineItemPricing(lineItem).totalDiscountDisplayedInSubtotal,
    0
  );

export const getTotalSavings = (state: CartStore) => {
  const lineItems = state.currentOrder.line_items;
  // Sometimes state.currentOrder.line_items is undefined so we need to catch
  // that and then report to bugsnag to diagnose
  if (lineItems === undefined) {
    reportMissingLineItems(state);
    return 0;
  }
  return getTotalSavingsForLineItems(lineItems);
};

export const getLineItemsAtCartOpen = (state: CartStore) => {
  const order = { ...state.currentOrder };
  const lineItems = order.line_items.slice();
  if (state.currentOrderAtCartOpen) {
    // we restore any 0 quantity line_items that existed
    // when the cart was first opened so that the user can
    // correct an accidental additional tap
    state.currentOrderAtCartOpen.line_items.forEach(
      (lineItemOrig: LineItem, index: number) => {
        if (!lineItems.find(item => item.sku === lineItemOrig.sku)) {
          lineItems.splice(index, 0, {
            ...lineItemOrig,
            quantity: 0,
          });
        }
      }
    );
  }
  return lineItems;
};

export const getLineItemCommitmentRewardAdjustmentTotal = (
  lineItem: LineItem
) => Math.abs(lineItem.commitment_reward_adjustment_total as number);

export const getLineItemAutoShipDiscount = (lineItem: LineItem) =>
  Math.abs(lineItem.membership_discount_total as number);

export const getLineItemsWithSubscribableValue = (
  state: CartStore,
  value: boolean
) =>
  state.currentOrder.line_items.filter(item => {
    const variant = variantWithSku(item.sku, state);
    return variant && variant.is_subscribable === value;
  });

export const getLineItemsSubscribable = (state: CartStore) =>
  getLineItemsWithSubscribableValue(state, true);

export const getLineItems = (state: CartStore) => state.currentOrder.line_items;

export const getLineItemsRegular = (state: CartStore) =>
  getLineItemsWithSubscribableValue(state, false);

export const getLineItemsTotalQuantity = (lineItems: LineItem[]) =>
  lineItems.reduce((total, item) => total + item.quantity, 0);

export const getLineItemsSubscribableQuantity = (state: CartStore) =>
  getLineItemsTotalQuantity(getLineItemsSubscribable(state));

export const getTotalPriceOfLineItems = (lineItems: LineItem[]) =>
  lineItems.reduce(
    (total, lineItem) =>
      total +
      parseFloat(lineItem.price as string) * lineItem.quantity -
      buildLineItemPricing(lineItem).totalDiscount,
    0
  );

// second argument allows us to easily mock the selector
export const getNumOfSubscribableItemsReqToCheckout = (
  state: CartStore,
  subscribableQuantityFn = getLineItemsSubscribableQuantity
) => {
  const { minimumSubscriptionItems } = state;
  const isMembershipOrder = state.currentOrder.membership_order;
  const hasSubscriptionCommitment =
    state.currentOrder.has_subscription_commitment;
  const subscribableQuantity = subscribableQuantityFn(state);
  if (isMembershipOrder || hasSubscriptionCommitment) {
    return Math.max(minimumSubscriptionItems - subscribableQuantity, 0);
  }
  return 0;
};

export const getLineItemQuantitySaved = (state: CartStore, props) => {
  const { currentOrder } = state;
  const lineItem = currentOrder.line_items.find(
    item => item.variant_offering_id === props.offeringId
  );
  return lineItem ? lineItem.quantity : 0;
};

export const getLineItemQuantityPending = (state: CartStore, props) => {
  const { lineItemsPending } = state;
  const lineItemPending = lineItemsPending.find(
    item => item.offeringId === props.offeringId
  );
  return lineItemPending
    ? lineItemPending.quantity
    : getLineItemQuantitySaved(state, props);
};

export const getVariantForVariantOffering = (
  variantOffering,
  state: CartStore
) => variantWithSku(variantOffering.variantSku, state);

export const getOfferingName = (variantOffering, state) => {
  const variant =
    variantOffering && variantWithSku(variantOffering.variantSku, state);
  return variant && variant.name;
};

export const getLineItemWithVariantOffering = (
  variantOffering,
  state: CartStore
) =>
  state.currentOrder.line_items.find(
    lineItem => lineItem.variant_offering_id === variantOffering.id
  );

export const isConvertOrderToSubscriptionProcessing = (
  order: Order,
  state: CartStore
) => {
  if (state.convertingOrderToSubscription) {
    return order.number === state.convertingOrderToSubscription.number;
  }
};

export const getShouldAutoShowCommitmentUpsellModal = (
  stateNew: CartStore,
  statePrev: CartStore
) => {
  const { evergreenOffering } = stateNew;

  // short circuit so all code below can assume we have the object
  if (!evergreenOffering) {
    return false;
  }

  const { isSoldOut } = evergreenOffering;
  const isAlreadyInCart = !!getLineItemWithVariantOffering(
    evergreenOffering,
    stateNew
  );

  const orderPrev = statePrev.currentOrder;
  const orderNew = stateNew.currentOrder;

  const isTransitioningToMembershipOrder =
    !orderPrev.membership_order && orderNew.membership_order;
  return !isSoldOut && !isAlreadyInCart && isTransitioningToMembershipOrder;
};
